import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Settings, Building2, Landmark, Users, DollarSign, AlertCircle, CheckCircle2, Download, RefreshCw, Trash2, FileInput, Save, Plus } from 'lucide-react';
import Header from '../../components/navigation/Header';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import systemControlService from '../../services/systemControlService';
import payrollService from '../../services/payrollService';
import bankTransferService from '../../services/bankTransferService';
import approvalWorkflowService from '../../services/approvalWorkflowService';
import employeeService from '../../services/employeeService';
import leaveService from '../../services/leaveService';

export default function PayrollProcessingBankingControlCenter() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // System Control Data
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [payrollSettings, setPayrollSettings] = useState([]);
  const [bankConfigs, setBankConfigs] = useState([]);
  const [glMappings, setGLMappings] = useState([]);

  // Employee Data
  const [employees, setEmployees] = useState([]);
  const [employeeLoans, setEmployeeLoans] = useState([]);
  const [employeeFlags, setEmployeeFlags] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);

  // Payroll Processing
  const [payrollRuns, setPayrollRuns] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [reversePayRequests, setReversePayRequests] = useState([]);
  const [purgePayRequests, setPurgePayRequests] = useState([]);
  const [importSessions, setImportSessions] = useState([]);

  // Bank Transfers
  const [transferBatches, setTransferBatches] = useState([]);

  // Reports
  const [nasfundContributions, setNasfundContributions] = useState([]);
  const [swtFilings, setSwtFilings] = useState([]);

  // Modal States
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [showDepartmentModal, setShowDepartmentModal] = useState(false);
  const [showLoanModal, setShowLoanModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showReversePayModal, setShowReversePayModal] = useState(false);
  const [showPurgePayModal, setShowPurgePayModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreatePayModal, setShowCreatePayModal] = useState(false);

  // Form States
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedPayrollRun, setSelectedPayrollRun] = useState('');
  const [selectedBank, setSelectedBank] = useState('');

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      setLoading(true);
      setError('');

      const [
        branchesData,
        departmentsData,
        settingsData,
        banksData,
        glData,
        employeesData,
        loansData,
        flagsData,
        leaveData,
        payrollData,
        approvalsData,
        reverseData,
        purgeData,
        importData,
        nasfundData,
        swtData
      ] = await Promise.all([
        systemControlService?.getAllBranches(),
        systemControlService?.getAllDepartments(),
        systemControlService?.getAllPayrollSettings(),
        systemControlService?.getAllBankConfigurations(),
        systemControlService?.getAllGLMappings(),
        employeeService?.getAllEmployees(),
        systemControlService?.getAllLoans(),
        systemControlService?.getAllFlags(),
        leaveService?.getAllRequests(),
        payrollService?.getAllRuns(),
        approvalWorkflowService?.getPendingApprovals(),
        systemControlService?.getAllReversePayRequests(),
        systemControlService?.getAllPurgePayRequests(),
        systemControlService?.getAllImportSessions(),
        payrollService?.getNasfundContributions(),
        payrollService?.getSWTFilings()
      ]);

      setBranches(branchesData);
      setDepartments(departmentsData);
      setPayrollSettings(settingsData);
      setBankConfigs(banksData);
      setGLMappings(glData);
      setEmployees(employeesData);
      setEmployeeLoans(loansData);
      setEmployeeFlags(flagsData);
      setLeaveRequests(leaveData);
      setPayrollRuns(payrollData);
      setPendingApprovals(approvalsData);
      setReversePayRequests(reverseData);
      setPurgePayRequests(purgeData);
      setImportSessions(importData);
      setNasfundContributions(nasfundData);
      setSwtFilings(swtData);
    } catch (err) {
      setError(err?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBranch = async (e) => {
    e?.preventDefault();
    try {
      const formData = new FormData(e?.target);
      await systemControlService?.createBranch({
        branchCode: formData?.get('branchCode'),
        branchName: formData?.get('branchName'),
        branchType: formData?.get('branchType'),
        city: formData?.get('city'),
        province: formData?.get('province'),
        isActive: true
      });
      setSuccess('Branch created successfully');
      setShowBranchModal(false);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to create branch');
    }
  };

  const handleCreateDepartment = async (e) => {
    e?.preventDefault();
    try {
      const formData = new FormData(e?.target);
      await systemControlService?.createDepartment({
        departmentCode: formData?.get('departmentCode'),
        departmentName: formData?.get('departmentName'),
        branchId: formData?.get('branchId'),
        costCenterCode: formData?.get('costCenterCode'),
        isActive: true
      });
      setSuccess('Department created successfully');
      setShowDepartmentModal(false);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to create department');
    }
  };

  const handleCreateLoan = async (e) => {
    e?.preventDefault();
    try {
      const formData = new FormData(e?.target);
      const loanAmount = parseFloat(formData?.get('loanAmount'));
      const repaymentMonths = parseInt(formData?.get('repaymentMonths'));
      const monthlyDeduction = loanAmount / repaymentMonths;

      await systemControlService?.createLoan({
        employeeId: formData?.get('employeeId'),
        loanNumber: `LOAN-${Date?.now()}`,
        loanType: formData?.get('loanType'),
        loanAmount: loanAmount,
        interestRate: parseFloat(formData?.get('interestRate')) || 0,
        repaymentPeriodMonths: repaymentMonths,
        monthlyDeduction: monthlyDeduction,
        startDate: formData?.get('startDate'),
        approvedBy: 'admin@example.com',
        notes: formData?.get('notes')
      });
      setSuccess('Employee loan created successfully');
      setShowLoanModal(false);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to create loan');
    }
  };

  const handleCreateFlag = async (e) => {
    e?.preventDefault();
    try {
      const formData = new FormData(e?.target);
      await systemControlService?.createFlag({
        employeeId: formData?.get('employeeId'),
        flagType: formData?.get('flagType'),
        flagReason: formData?.get('flagReason'),
        severity: formData?.get('severity'),
        flaggedBy: 'admin@example.com'
      });
      setSuccess('Employee flagged successfully');
      setShowFlagModal(false);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to flag employee');
    }
  };

  const handleCreateReversePay = async (e) => {
    e?.preventDefault();
    try {
      const formData = new FormData(e?.target);
      await systemControlService?.createReversePayRequest({
        payrollRunId: formData?.get('payrollRunId'),
        reversalReason: formData?.get('reversalReason'),
        affectedEmployees: parseInt(formData?.get('affectedEmployees')),
        totalAmountReversed: parseFloat(formData?.get('totalAmount')),
        requestedBy: 'admin@example.com'
      });
      setSuccess('Reverse pay request submitted for approval');
      setShowReversePayModal(false);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to create reverse pay request');
    }
  };

  const handleCreatePurgePay = async (e) => {
    e?.preventDefault();
    try {
      const formData = new FormData(e?.target);
      await systemControlService?.createPurgePayRequest({
        payrollRunId: formData?.get('payrollRunId'),
        purgeReason: formData?.get('purgeReason'),
        affectedRecords: parseInt(formData?.get('affectedRecords')),
        dataRetentionPeriodDays: parseInt(formData?.get('retentionDays')),
        requestedBy: 'admin@example.com'
      });
      setSuccess('Purge pay request submitted for approval');
      setShowPurgePayModal(false);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to create purge pay request');
    }
  };

  const handleGenerateBankTransfer = async (bankName) => {
    try {
      if (!selectedPayrollRun) {
        setError('Please select a payroll run first');
        return;
      }
      const result = await bankTransferService?.createTransferBatchFromPayrollRun(
        selectedPayrollRun,
        bankName,
        'admin@example.com'
      );
      setSuccess(`Bank transfer batch created: ${result?.batchNumber}`);
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to generate bank transfer');
    }
  };

  const handleApproveReversePay = async (id) => {
    try {
      await systemControlService?.approveReversePayRequest(id, 'admin@example.com');
      setSuccess('Reverse pay request approved');
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to approve reverse pay');
    }
  };

  const handleApprovePurgePay = async (id) => {
    try {
      await systemControlService?.approvePurgePayRequest(id, 'admin@example.com');
      setSuccess('Purge pay request approved');
      loadAllData();
    } catch (err) {
      setError(err?.message || 'Failed to approve purge pay');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>Payroll Processing & Banking Control Center - PNG HR Analytics</title>
        <meta name="description" content="Comprehensive payroll management with system control files, employee file maintenance, pay processing workflows, bank transfers, and government reporting" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Page Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Settings className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-heading font-bold text-foreground">Payroll Processing & Banking Control Center</h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    End-to-end payroll management with automated bank transfers and comprehensive reporting
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Alert Messages */}
        {error && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-destructive/90 mt-1">{error}</p>
              </div>
              <button onClick={() => setError('')} className="ml-auto text-destructive hover:text-destructive/80">
                ×
              </button>
            </div>
          </div>
        )}

        {success && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
            <div className="p-4 bg-success/10 border border-success/20 rounded-lg flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-success">Success</h3>
                <p className="text-sm text-success/90 mt-1">{success}</p>
              </div>
              <button onClick={() => setSuccess('')} className="ml-auto text-success hover:text-success/80">
                ×
              </button>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <div className="bg-card border border-border rounded-lg p-2">
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('dashboard')}
              >
                Dashboard
              </Button>
              <Button
                variant={activeTab === 'organization' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('organization')}
              >
                Organization Info
              </Button>
              <Button
                variant={activeTab === 'payroll-settings' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('payroll-settings')}
              >
                Payroll Settings
              </Button>
              <Button
                variant={activeTab === 'bank-setup' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('bank-setup')}
              >
                Bank Setup
              </Button>
              <Button
                variant={activeTab === 'gl-interface' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('gl-interface')}
              >
                GL Interface
              </Button>
              <Button
                variant={activeTab === 'employee-maintenance' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('employee-maintenance')}
              >
                Employee Maintenance
              </Button>
              <Button
                variant={activeTab === 'pay-processing' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('pay-processing')}
              >
                Pay Processing
              </Button>
              <Button
                variant={activeTab === 'reports' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab('reports')}
              >
                Reports
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Employees</p>
                    <p className="text-3xl font-bold text-foreground mt-2">{employees?.length}</p>
                  </div>
                  <Users className="h-10 w-10 text-primary opacity-20" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Pending Approvals</p>
                    <p className="text-3xl font-bold text-warning mt-2">{pendingApprovals?.length}</p>
                  </div>
                  <AlertCircle className="h-10 w-10 text-warning opacity-20" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Loans</p>
                    <p className="text-3xl font-bold text-foreground mt-2">{employeeLoans?.filter(l => l?.loanStatus === 'active')?.length}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-success opacity-20" />
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Flags</p>
                    <p className="text-3xl font-bold text-destructive mt-2">{employeeFlags?.length}</p>
                  </div>
                  <AlertCircle className="h-10 w-10 text-destructive opacity-20" />
                </div>
              </div>

              {/* Quick Actions */}
              <div className="col-span-full bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button onClick={() => setShowCreatePayModal(true)} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Pay
                  </Button>
                  <Button onClick={() => setShowImportModal(true)} variant="outline" className="w-full">
                    <FileInput className="h-4 w-4 mr-2" />
                    Import Pay
                  </Button>
                  <Button onClick={() => setShowReversePayModal(true)} variant="outline" className="w-full">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Reverse Pay
                  </Button>
                  <Button onClick={() => setShowPurgePayModal(true)} variant="outline" className="w-full">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Purge Pay
                  </Button>
                </div>
              </div>

              {/* Recent Payroll Runs */}
              <div className="col-span-full bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Recent Payroll Runs</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Payment Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total Net (K)</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payrollRuns?.slice(0, 5)?.map((run) => (
                        <tr key={run?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm text-foreground">
                            {run?.payPeriodStart} - {run?.payPeriodEnd}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{run?.paymentDate}</td>
                          <td className="py-3 px-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              run?.payrollStatus === 'processed' ? 'bg-success/10 text-success' :
                              run?.payrollStatus === 'paid'? 'bg-primary/10 text-primary' : 'bg-warning/10 text-warning'
                            }`}>
                              {run?.payrollStatus}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-foreground">
                            K {run?.totalNet?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button size="sm" variant="ghost" onClick={() => setSelectedPayrollRun(run?.id)}>
                              Select
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Organization Info Tab */}
          {activeTab === 'organization' && (
            <div className="space-y-6">
              {/* Branches */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    Organization Branches
                  </h2>
                  <Button onClick={() => setShowBranchModal(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Branch
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Code</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Branch Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">City</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Province</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {branches?.map((branch) => (
                        <tr key={branch?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm font-medium text-foreground">{branch?.branchCode}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{branch?.branchName}</td>
                          <td className="py-3 px-4 text-sm text-foreground capitalize">{branch?.branchType}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{branch?.city}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{branch?.province}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              branch?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                            }`}>
                              {branch?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Departments */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Departments</h2>
                  <Button onClick={() => setShowDepartmentModal(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Department
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Code</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Department Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Branch</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Cost Center</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {departments?.map((dept) => (
                        <tr key={dept?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm font-medium text-foreground">{dept?.departmentCode}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{dept?.departmentName}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{dept?.branchName || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{dept?.costCenterCode || 'N/A'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              dept?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                            }`}>
                              {dept?.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Payroll Settings Tab */}
          {activeTab === 'payroll-settings' && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Payroll System Settings</h2>
              <div className="space-y-4">
                {payrollSettings?.map((setting) => (
                  <div key={setting?.id} className="flex items-center justify-between p-4 bg-accent/50 rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium text-foreground">{setting?.settingKey}</p>
                      <p className="text-sm text-muted-foreground mt-1">{setting?.description}</p>
                      <p className="text-sm text-primary mt-2">Value: {JSON.stringify(setting?.settingValue)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        setting?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                      }`}>
                        {setting?.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bank Setup Tab */}
          {activeTab === 'bank-setup' && (
            <div className="space-y-6">
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Landmark className="h-5 w-5 text-primary" />
                  Bank Transfer Configurations
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {bankConfigs?.map((bank) => (
                    <div key={bank?.id} className="p-4 border border-border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-foreground">{bank?.bankName}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          bank?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                        }`}>
                          {bank?.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Bank Code:</span>
                          <span className="font-medium text-foreground">{bank?.bankCode}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">File Format:</span>
                          <span className="font-medium text-foreground uppercase">{bank?.fileFormat}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Method:</span>
                          <span className="font-medium text-foreground uppercase">{bank?.transmissionMethod}</span>
                        </div>
                      </div>
                      <Button
                        onClick={() => handleGenerateBankTransfer(bank?.bankName)}
                        size="sm"
                        className="w-full mt-4"
                        disabled={!selectedPayrollRun}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Generate Transfer File
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payroll Run Selector */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="font-semibold text-foreground mb-4">Select Payroll Run for Bank Transfer</h3>
                <Select
                  options={payrollRuns?.filter(r => r?.payrollStatus === 'processed')?.map(run => ({
                    value: run?.id,
                    label: `${run?.payPeriodStart} to ${run?.payPeriodEnd} - K${run?.totalNet?.toLocaleString()}`
                  }))}
                  value={selectedPayrollRun}
                  onChange={setSelectedPayrollRun}
                  placeholder="Select payroll run..."
                />
              </div>
            </div>
          )}

          {/* GL Interface Tab */}
          {activeTab === 'gl-interface' && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">General Ledger Account Mappings</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Mapping Code</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Mapping Name</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Source Type</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">GL Account</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">GL Account Name</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {glMappings?.map((mapping) => (
                      <tr key={mapping?.id} className="border-b border-border hover:bg-accent/50">
                        <td className="py-3 px-4 text-sm font-medium text-foreground">{mapping?.mappingCode}</td>
                        <td className="py-3 px-4 text-sm text-foreground">{mapping?.mappingName}</td>
                        <td className="py-3 px-4 text-sm text-foreground capitalize">{mapping?.sourceType}</td>
                        <td className="py-3 px-4 text-sm font-medium text-primary">{mapping?.glAccountCode}</td>
                        <td className="py-3 px-4 text-sm text-foreground">{mapping?.glAccountName}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            mapping?.isActive ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                          }`}>
                            {mapping?.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Employee Maintenance Tab */}
          {activeTab === 'employee-maintenance' && (
            <div className="space-y-6">
              {/* Employee Loans */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Employee Loans</h2>
                  <Button onClick={() => setShowLoanModal(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Loan
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Loan #</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Amount (K)</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Monthly (K)</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Balance (K)</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeLoans?.map((loan) => (
                        <tr key={loan?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm font-medium text-foreground">{loan?.loanNumber}</td>
                          <td className="py-3 px-4 text-sm text-foreground">
                            {loan?.employee?.firstName} {loan?.employee?.lastName}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground capitalize">{loan?.loanType}</td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            {loan?.loanAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            {loan?.monthlyDeduction?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-foreground">
                            {loan?.balanceRemaining?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              loan?.loanStatus === 'active' ? 'bg-success/10 text-success' :
                              loan?.loanStatus === 'completed'? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                            }`}>
                              {loan?.loanStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Employee Flags */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Employee Flags</h2>
                  <Button onClick={() => setShowFlagModal(true)} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Flag Employee
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Flag Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Reason</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Severity</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Flagged Date</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeFlags?.map((flag) => (
                        <tr key={flag?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm text-foreground">
                            {flag?.employee?.firstName} {flag?.employee?.lastName}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground capitalize">{flag?.flagType}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{flag?.flagReason}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              flag?.severity === 'critical' ? 'bg-destructive/10 text-destructive' :
                              flag?.severity === 'high'? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                            }`}>
                              {flag?.severity}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">
                            {new Date(flag?.flaggedAt)?.toLocaleDateString()}
                          </td>
                          <td className="py-3 px-4 text-right">
                            <Button size="sm" variant="ghost">
                              Resolve
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Leave Requests */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Pending Leave Requests</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Employee</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Leave Type</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Start Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">End Date</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Days</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaveRequests?.filter(r => r?.leaveStatus === 'pending')?.slice(0, 10)?.map((request) => (
                        <tr key={request?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm text-foreground">
                            {request?.employee?.firstName} {request?.employee?.lastName}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground capitalize">{request?.leaveType}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{request?.startDate}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{request?.endDate}</td>
                          <td className="py-3 px-4 text-sm text-center font-medium text-foreground">{request?.daysRequested}</td>
                          <td className="py-3 px-4 text-center">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning">
                              {request?.leaveStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Pay Processing Tab */}
          {activeTab === 'pay-processing' && (
            <div className="space-y-6">
              {/* Pay Processing Workflow */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Pay Processing Workflow</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 border-2 border-primary rounded-lg text-center">
                    <div className="text-3xl font-bold text-primary mb-2">1</div>
                    <p className="font-semibold text-foreground">Create Pay</p>
                    <p className="text-xs text-muted-foreground mt-1">Setup pay period</p>
                  </div>
                  <div className="p-4 border-2 border-border rounded-lg text-center">
                    <div className="text-3xl font-bold text-muted-foreground mb-2">2</div>
                    <p className="font-semibold text-foreground">Manage Pay</p>
                    <p className="text-xs text-muted-foreground mt-1">Calculate & validate</p>
                  </div>
                  <div className="p-4 border-2 border-border rounded-lg text-center">
                    <div className="text-3xl font-bold text-muted-foreground mb-2">3</div>
                    <p className="font-semibold text-foreground">Approve Pay</p>
                    <p className="text-xs text-muted-foreground mt-1">Multi-level approval</p>
                  </div>
                  <div className="p-4 border-2 border-border rounded-lg text-center">
                    <div className="text-3xl font-bold text-muted-foreground mb-2">4</div>
                    <p className="font-semibold text-foreground">Process Pay</p>
                    <p className="text-xs text-muted-foreground mt-1">Bank transfers</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button onClick={() => setShowCreatePayModal(true)} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Pay Run
                  </Button>
                  <Button onClick={() => setShowImportModal(true)} variant="outline" className="w-full">
                    <FileInput className="h-4 w-4 mr-2" />
                    Import Pay from File
                  </Button>
                </div>
              </div>

              {/* Reverse Pay Requests */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Reverse Pay Requests</h2>
                  <Button onClick={() => setShowReversePayModal(true)} size="sm" variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Submit Reverse Pay
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Request #</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Pay Period</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Reason</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Employees</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Amount (K)</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reversePayRequests?.map((request) => (
                        <tr key={request?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm font-medium text-foreground">{request?.requestNumber}</td>
                          <td className="py-3 px-4 text-sm text-foreground">
                            {request?.payrollRun?.payPeriodStart} - {request?.payrollRun?.payPeriodEnd}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{request?.reversalReason}</td>
                          <td className="py-3 px-4 text-sm text-center text-foreground">{request?.affectedEmployees}</td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            {request?.totalAmountReversed?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request?.requestStatus === 'approved' ? 'bg-success/10 text-success' :
                              request?.requestStatus === 'rejected'? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'
                            }`}>
                              {request?.requestStatus}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {request?.requestStatus === 'pending' && (
                              <Button size="sm" onClick={() => handleApproveReversePay(request?.id)}>
                                Approve
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Purge Pay Requests */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Purge Pay Requests</h2>
                  <Button onClick={() => setShowPurgePayModal(true)} size="sm" variant="outline">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Submit Purge Pay
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Request #</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Pay Period</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Reason</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Records</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purgePayRequests?.map((request) => (
                        <tr key={request?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm font-medium text-foreground">{request?.requestNumber}</td>
                          <td className="py-3 px-4 text-sm text-foreground">
                            {request?.payrollRun?.payPeriodStart} - {request?.payrollRun?.payPeriodEnd}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{request?.purgeReason}</td>
                          <td className="py-3 px-4 text-sm text-center text-foreground">{request?.affectedRecords}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              request?.requestStatus === 'approved' ? 'bg-success/10 text-success' :
                              request?.requestStatus === 'rejected'? 'bg-destructive/10 text-destructive' : 'bg-warning/10 text-warning'
                            }`}>
                              {request?.requestStatus}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right">
                            {request?.requestStatus === 'pending' && (
                              <Button size="sm" onClick={() => handleApprovePurgePay(request?.id)}>
                                Approve
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Reports Tab */}
          {activeTab === 'reports' && (
            <div className="space-y-6">
              {/* NASFUND Report */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">NASFUND Contributions Report</h2>
                  <Button size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Generate Report
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Due Date</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Employee (K)</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Employer (K)</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total (K)</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nasfundContributions?.map((contrib) => (
                        <tr key={contrib?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm text-foreground">{contrib?.contributionPeriod}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{contrib?.dueDate}</td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            {contrib?.totalEmployeeContribution?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-sm text-right text-foreground">
                            {contrib?.totalEmployerContribution?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-foreground">
                            {(contrib?.totalEmployeeContribution + contrib?.totalEmployerContribution)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              contrib?.filingStatus === 'submitted' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                            }`}>
                              {contrib?.filingStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Nabawan Super Report (SWT) */}
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-foreground">Nabawan Super (SWT) Report</h2>
                  <Button size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Generate Report
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Period</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Due Date</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Total SWT (K)</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Submission Date</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {swtFilings?.map((filing) => (
                        <tr key={filing?.id} className="border-b border-border hover:bg-accent/50">
                          <td className="py-3 px-4 text-sm text-foreground">{filing?.filingPeriod}</td>
                          <td className="py-3 px-4 text-sm text-foreground">{filing?.dueDate}</td>
                          <td className="py-3 px-4 text-sm text-right font-medium text-foreground">
                            {filing?.totalSwt?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="py-3 px-4 text-sm text-foreground">{filing?.submissionDate || 'Not submitted'}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              filing?.filingStatus === 'submitted' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
                            }`}>
                              {filing?.filingStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showBranchModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Add New Branch</h2>
                <form onSubmit={handleCreateBranch} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Branch Code" name="branchCode" required />
                    <Input label="Branch Name" name="branchName" required />
                    <Input label="Branch Type" name="branchType" required />
                    <Input label="City" name="city" required />
                    <Input label="Province" name="province" required />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowBranchModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      Save Branch
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showDepartmentModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Add New Department</h2>
                <form onSubmit={handleCreateDepartment} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Department Code" name="departmentCode" required />
                    <Input label="Department Name" name="departmentName" required />
                    <Select
                      label="Branch"
                      name="branchId"
                      options={branches?.map(b => ({ value: b?.id, label: b?.branchName }))}
                      required
                    />
                    <Input label="Cost Center Code" name="costCenterCode" />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowDepartmentModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      Save Department
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showLoanModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Create Employee Loan</h2>
                <form onSubmit={handleCreateLoan} className="space-y-4">
                  <Select
                    label="Employee"
                    name="employeeId"
                    options={employees?.map(e => ({ value: e?.id, label: `${e?.firstName} ${e?.lastName} (${e?.employeeNumber})` }))}
                    required
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Loan Type" name="loanType" required placeholder="e.g., Personal, Emergency" />
                    <Input label="Loan Amount (K)" name="loanAmount" type="number" step="0.01" required />
                    <Input label="Interest Rate (%)" name="interestRate" type="number" step="0.01" defaultValue="0" />
                    <Input label="Repayment Period (Months)" name="repaymentMonths" type="number" required />
                    <Input label="Start Date" name="startDate" type="date" required />
                  </div>
                  <Input label="Notes" name="notes" />
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowLoanModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      Create Loan
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showFlagModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Flag Employee</h2>
                <form onSubmit={handleCreateFlag} className="space-y-4">
                  <Select
                    label="Employee"
                    name="employeeId"
                    options={employees?.map(e => ({ value: e?.id, label: `${e?.firstName} ${e?.lastName} (${e?.employeeNumber})` }))}
                    required
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Flag Type" name="flagType" required placeholder="e.g., Compliance, Performance" />
                    <Select
                      label="Severity"
                      name="severity"
                      options={[
                        { value: 'low', label: 'Low' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'high', label: 'High' },
                        { value: 'critical', label: 'Critical' }
                      ]}
                      required
                    />
                  </div>
                  <Input label="Flag Reason" name="flagReason" required />
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowFlagModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      Flag Employee
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showReversePayModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Submit Reverse Pay Request</h2>
                <form onSubmit={handleCreateReversePay} className="space-y-4">
                  <Select
                    label="Payroll Run"
                    name="payrollRunId"
                    options={payrollRuns?.map(run => ({
                      value: run?.id,
                      label: `${run?.payPeriodStart} to ${run?.payPeriodEnd} - ${run?.payrollStatus}`
                    }))}
                    required
                  />
                  <Input label="Reversal Reason" name="reversalReason" required />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Affected Employees" name="affectedEmployees" type="number" required />
                    <Input label="Total Amount (K)" name="totalAmount" type="number" step="0.01" required />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowReversePayModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      Submit Request
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {showPurgePayModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-foreground mb-4">Submit Purge Pay Request</h2>
                <form onSubmit={handleCreatePurgePay} className="space-y-4">
                  <Select
                    label="Payroll Run"
                    name="payrollRunId"
                    options={payrollRuns?.map(run => ({
                      value: run?.id,
                      label: `${run?.payPeriodStart} to ${run?.payPeriodEnd} - ${run?.payrollStatus}`
                    }))}
                    required
                  />
                  <Input label="Purge Reason" name="purgeReason" required />
                  <div className="grid grid-cols-2 gap-4">
                    <Input label="Affected Records" name="affectedRecords" type="number" required />
                    <Input label="Retention Period (Days)" name="retentionDays" type="number" defaultValue="90" required />
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button type="button" variant="outline" onClick={() => setShowPurgePayModal(false)}>
                      Cancel
                    </Button>
                    <Button type="submit">
                      <Save className="h-4 w-4 mr-2" />
                      Submit Request
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </React.Fragment>
  );
}